import { Alert, Button, InfiniteTable, InfiniteTableColumns, InfiniteTableSortBy, Stack } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';

import { CompanyPlatform } from '../../company';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { Routes } from '../../routes';

export interface PlatformListProps {
  data: {
    platforms: CompanyPlatform[];
    company_id: string;
  };
}

interface PlatformRow extends CompanyPlatform {
  id: string;
}

const getSortValue = (row: PlatformRow, key: keyof PlatformRow): string => {
  switch (key) {
    case 'platform_id':
      return row.platform_id;
    case 'platform_name':
      return row.platform_name;
    case 'company_id_on_platform':
      return row.company_id_on_platform;
    case 'created_at':
      return row.created_at;
    default:
      return '';
  }
};

const sortData = (data: PlatformRow[], sortBy: InfiniteTableSortBy<keyof PlatformRow>): PlatformRow[] => {
  return [...data].sort((a, b) => {
    const aVal = getSortValue(a, sortBy.columnId);
    const bVal = getSortValue(b, sortBy.columnId);
    const cmp = aVal.localeCompare(bVal);
    return sortBy.order === 'ASC' ? cmp : -cmp;
  });
};

export const PlatformList: React.FC<PlatformListProps> = ({ data }) => {
  const { slug_company_name } = useParams<{ slug_company_name?: string }>();
  const navigate = useNavigate();
  const { platforms, company_id } = data;
  const isAdmin = useIsAdmin();
  const hasPlatformsPermission = useHasScopes([BackofficeScope.companyWritePlatforms]);

  const [sortBy, setSortBy] = useState<InfiniteTableSortBy<keyof PlatformRow>>({
    columnId: 'platform_id',
    order: 'ASC',
  });

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  const tableData = useMemo(
    () =>
      sortData(
        platforms.map((p) => ({ ...p, id: p.platform_id })),
        sortBy,
      ),
    [platforms, sortBy],
  );

  const columns: InfiniteTableColumns<PlatformRow> = useMemo(
    () => [
      {
        id: 'platform_id',
        title: '#',
        width: '10%',
        minWidth: '60px',
        isUserSortable: true,
        align: 'end' as const,
        cellContent: ({ row }) => <>{row.platform_id}</>,
      },
      {
        id: 'platform_name',
        title: 'Name',
        width: '30%',
        minWidth: '150px',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink
            to={Routes.CompanyPlatform.generatePath({
              company_id,
              platform_id: row.platform_id,
              slug_company_name,
            })}>
            {row.platform_name}
          </NavLink>
        ),
      },
      {
        id: 'company_id_on_platform',
        title: 'Company ID on platform',
        width: '25%',
        minWidth: '150px',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.company_id_on_platform}</>,
      },
      {
        id: 'created_at',
        title: 'Created at',
        width: '35%',
        minWidth: '150px',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <>
            <div>
              <FormattedDate date={row.created_at} />
            </div>
            {row.created_by ? (
              <>
                by{' '}
                <NavLink to={Routes.User.generatePath({ user_id: row.created_by.user_id })}>
                  {`${row.created_by.first_name ?? ''} ${row.created_by.last_name ?? ''}`.trim()}
                </NavLink>
              </>
            ) : null}
          </>
        ),
      },
    ],
    [company_id, slug_company_name],
  );

  return (
    <Stack direction="column" gap="medium">
      <Stack direction="row" gap="medium" alignItems="center">
        <h2 style={{ margin: 0 }}>Platforms</h2>
        {(isAdmin || hasPlatformsPermission) && (
          <Button
            onClick={() => navigate(Routes.CompanyPlatformCreate.generatePath({ company_id, slug_company_name }))}
            kind="primary"
            size="small">
            Add Platform
          </Button>
        )}
      </Stack>

      <Alert kind="warning">
        <strong>This is powerful, use with precaution!</strong> This will potentially have an impact on vehicles, tours
        and sites communicated to and from the selected platform
      </Alert>

      <div className="sub-table-scroll-container" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div ref={containerRefCallback}>
          {scrollParent !== null && (
            <InfiniteTable<PlatformRow>
              columns={columns}
              data={tableData}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              scrollParent={scrollParent}
              className="compact-table"
            />
          )}
        </div>
      </div>
    </Stack>
  );
};
